import React from 'react';
import { VocalPack } from '@/types';
import { Mic, Check, Volume2, Users } from 'lucide-react';
import { Badge } from '../ui';

type VocalProfileCardVariant = 'default' | 'browser';

interface VocalProfileCardProps {
  pack: VocalPack;
  packKey: string;
  variant?: VocalProfileCardVariant;
  isSelected?: boolean;
  isExcluded?: boolean;
  onClick?: () => void;
}

/** Parse comma-separated styles string into tag array */
function parseStyles(styles: string): string[] {
  return styles
    .split(',')
    .map(s => s.trim().replace(/_/g, ' '))
    .filter(Boolean);
}

const VocalProfileCard: React.FC<VocalProfileCardProps> = ({
  pack,
  variant = 'default',
  isSelected = false,
  isExcluded = false,
  onClick,
}) => {
  if (variant === 'browser') {
    const styleTags = parseStyles(pack.styles);
    const hasAltVoices = !!(pack.voiceAltA || pack.voiceAltB);

    return (
      <button
        type="button"
        onClick={onClick}
        className={`
          relative text-left w-full p-4 rounded-2xl border-2 transition-all
          hover:border-accent/50 hover:bg-slate-800/40 active:scale-[0.98]
          ${isSelected
            ? 'border-accent bg-accent/5 shadow-lg shadow-amber-500/10'
            : isExcluded
              ? 'border-slate-800 bg-slate-950/40 opacity-50'
              : 'border-slate-800 bg-slate-950/40'
          }
        `}
      >
        {/* Selected check badge */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-accent flex items-center justify-center text-slate-950">
            <Check size={14} strokeWidth={3} />
          </div>
        )}

        {/* Excluded label */}
        {isExcluded && (
          <div className="absolute top-3 right-3">
            <Badge variant="default-muted" size="xs">In other slot</Badge>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-accent/10 rounded-lg text-accent flex-shrink-0">
            <Mic size={18} />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight truncate">
              {pack.name.replace('VocalForge: ', '')}
            </h4>
          </div>
        </div>

        {/* Description */}
        <p className="text-[11px] text-slate-400 leading-relaxed italic line-clamp-2 mb-3">"{pack.description}"</p>

        {/* Style tags */}
        <div className="flex flex-wrap gap-1 mb-3 overflow-hidden">
          {styleTags.slice(0, 4).map(tag => (
            <Badge key={tag} variant="primary-muted" size="xs" pill className="font-bold uppercase max-w-full">
              {tag}
            </Badge>
          ))}
          {styleTags.length > 4 && (
            <Badge variant="default-muted" size="xs" pill>+{styleTags.length - 4}</Badge>
          )}
        </div>

        {/* Metadata rows */}
        <div className="space-y-2 pt-3 border-t border-slate-800/60">
          {/* Lead voice */}
          <div className="flex items-start gap-2">
            <div className="flex items-center gap-1 text-slate-400 flex-shrink-0 mt-0.5">
              <Volume2 size={10} />
              <span className="text-[11px] font-black uppercase tracking-widest">Lead</span>
            </div>
            <span className="text-[11px] text-slate-300 line-clamp-1">
              {pack.voiceLead.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Alt voices indicator */}
          {hasAltVoices && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-slate-400 flex-shrink-0">
                <Users size={10} />
                <span className="text-[11px] font-black uppercase tracking-widest">Roles</span>
              </div>
              <div className="flex gap-1">
                <Badge variant="info-muted" size="xs" pill className="font-bold">Lead</Badge>
                {pack.voiceAltA && <Badge variant="info-muted" size="xs" pill className="font-bold">Alt A</Badge>}
                {pack.voiceAltB && <Badge variant="info-muted" size="xs" pill className="font-bold">Alt B</Badge>}
              </div>
            </div>
          )}

          {/* Mix character */}
          <div className="flex items-start gap-2">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex-shrink-0 mt-0.5">Mix</span>
            <span className="text-[11px] text-slate-400 line-clamp-1">
              {pack.mixTargets.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </button>
    );
  }

  // Default variant - simple info card
  return (
    <div className="bg-slate-950 border-l-4 border-l-accent rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-accent/10 rounded-lg text-accent">
          <Mic size={18} />
        </div>
        <div>
          <h4 className="text-sm font-black text-slate-100 uppercase tracking-tight">
            {pack.name.replace('VocalForge: ', '')}
          </h4>
        </div>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed italic">"{pack.description}"</p>
      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-900">
        <div className="space-y-1">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Lead Voice</span>
          <span className="text-[11px] text-slate-400 block line-clamp-1">{pack.voiceLead.replace(/_/g, ' ')}</span>
        </div>
        <div className="space-y-1">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest block">Physics</span>
          <span className="text-[11px] text-slate-400 block line-clamp-1">{pack.physics.replace(/_/g, ' ')}</span>
        </div>
      </div>
    </div>
  );
};

export default VocalProfileCard;
